import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Checkbox, Select } from '@ferris/react/components/forms';
import addressFormConfig from '@homeaway/stab-address-form-configurator';
import LocationField from '../AutoComplete/LocationField';
import { ADDRESS_FIELDS, keysToFields } from '../AutoComplete/AddressFormats';
import { retrieveData } from '../../../../actions/graphQlSvc';
import TypeAheadField from './TypeAhead';
import { FETCH_COUNTRY_DATA, FETCH_ENTITY, getFieldOrder } from './util';
import { regex } from '../../../../middleware/validation/rules';

const useDidMountEffect = (func, deps) => {
    const didMount = useRef(false);

    useEffect(() => {
        if (didMount.current) func();
        else didMount.current = true;
    }, deps);
};

const getCityOptions = (list, isCl, stateName) => list.map(city => ({
    stateId: city.ancestorId,
    state: stateName || city.ancestorLocalization,
    localization: city.localization,
    // eslint-disable-next-line max-len
    value: (isCl && !regex.latinCharacterRegex.test(city.localization)) ? city.name : city.localization,
    cityState: `${city.localization} ${stateName || city.ancestorLocalization}`,
    stateCity: `${stateName || city.ancestorLocalization} ${city.localization}`
}));

const emptyCityOption = {
    stateId: '',
    state: '',
    localization: '',
    cityState: '',
    stateCity: '',
    value: ''
};

const emptyOption = {
    text: '---',
    value: '|'
};

const getStateOptions = (list, isCl) => {
    const result = list.map(state => ({
        text: state.localization,
        value: `${state.id}|${state.abbrv || ((isCl && !regex.latinCharacterRegex.test(state.localization)) ? state.name : state.localization)}`
    }));
    result.unshift(emptyOption);
    return result;
};

export const getFormInput = (fieldLabelKey, label, value, changeHandler, error, brand) => (
    <LocationField
        key={fieldLabelKey}
        fieldLabelKey={fieldLabelKey}
        fieldLabelTranslation={label}
        value={value}
        onChangeHandler={changeHandler}
        errorMessage={error}
        classes={`form-group ${fieldLabelKey} ${brand}`}
    />
);

const FieldR = (props) => {
    const {
        value,
        fieldError,
        addressFieldKey,
        brand,
        labelTranslation,
        onChange,
        useGeoFeeds,
        isAddressV2
    } = props;

    const [visited, setVisited] = useState(false);
    const onBlur = () => {
        setVisited(true);
    };

    const [error, setError] = useState('');

    useEffect(() => {
        if (!isAddressV2 || visited || addressFieldKey === ADDRESS_FIELDS.ADDRESS2) {
            setError(fieldError);
        }
    }, [fieldError, visited]);

    let ComponentToUse = LocationField;
    const baseProps = {
        key: addressFieldKey,
        fieldLabelKey: addressFieldKey,
        fieldLabelTranslation: labelTranslation,
        value,
        onChangeHandler: onChange,
        errorMessage: error,
        classes: `form-group ${addressFieldKey} ${brand}`,
        onChange,
        label: labelTranslation,
        onBlur
    };
    const customProps = {};
    switch (addressFieldKey) {
    case ADDRESS_FIELDS.COUNTRY:
    case ADDRESS_FIELDS.STATE_PROVINCE:
        // eslint-disable-next-line no-case-declarations
        const {
            countryList, statesList, handleStateOptionSelect, selectedStateOption
        } = props;
        // eslint-disable-next-line no-case-declarations
        const isState = addressFieldKey === ADDRESS_FIELDS.STATE_PROVINCE;
        // eslint-disable-next-line max-len
        if (isState && !(statesList && statesList.length > 1)) break;
        ComponentToUse = Select;
        customProps.fullWidth = true;
        customProps.name = addressFieldKey;
        customProps.formControlProps = { id: `${addressFieldKey}` };
        customProps.invalid = error.length;
        customProps.floatLabel = true;
        customProps.isControlled = true;
        customProps.type = 'text';
        customProps.validationMessage = error || '';
        customProps.optionList = isState ? statesList : countryList;
        customProps.onChange = (isState && useGeoFeeds) ? handleStateOptionSelect : onChange;
        customProps.value = (isState && useGeoFeeds) ? selectedStateOption.value : value;
        break;
    case ADDRESS_FIELDS.CITY:
        // eslint-disable-next-line no-case-declarations
        const {
            citiesList, selectedCityOption, handleCityOptionSelect
        } = props;
        if (!(useGeoFeeds && citiesList && citiesList.length)) break;
        ComponentToUse = TypeAheadField;
        customProps.onSelect = onChange;
        customProps.catalog = citiesList;
        customProps.id = addressFieldKey;
        customProps.name = addressFieldKey;
        customProps.selectedOption = selectedCityOption;
        customProps.handleSelect = handleCityOptionSelect;
        break;
    case ADDRESS_FIELDS.ADDRESS2:
        // eslint-disable-next-line no-case-declarations
        const {
            displayUNCheckbox, setConfirmNoUnitNumber, noUnitTranlsation, confirmNoUnitNumber
        } = props;

        if (!displayUNCheckbox()) break;
        // eslint-disable-next-line no-case-declarations
        const handleCheck = (e) => {
            setConfirmNoUnitNumber(e.target.checked);
        };
        return (
            <div className="input-checkbox">
                <LocationField
                    {
                    ...{
                        ...baseProps,
                        errorMessage: confirmNoUnitNumber ? '' : error
                    }
                    }
                />
                <Checkbox
                    name="no-unit-number-confirmation"
                    label={noUnitTranlsation}
                    onChange={handleCheck}
                />
            </div>
        );
    default:
        break;
    }
    return (
        <ComponentToUse
            {
            ...{
                ...baseProps,
                ...customProps
            }
            }
        />
    );
};

FieldR.propTypes = {
    value: PropTypes.string.isRequired,
    fieldError: PropTypes.string.isRequired,
    addressFieldKey: PropTypes.string.isRequired,
    brand: PropTypes.string.isRequired,
    labelTranslation: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    countryList: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string
    })).isRequired,
    statesList: PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.string,
        value: PropTypes.string
    })),
    citiesList: PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.string,
        value: PropTypes.string
    })),
    selectedCityOption: PropTypes.shape({
        text: PropTypes.string,
        value: PropTypes.string
    }),
    handleCityOptionSelect: PropTypes.func,
    useGeoFeeds: PropTypes.bool.isRequired,
    setConfirmNoUnitNumber: PropTypes.func.isRequired,
    displayUNCheckbox: PropTypes.func.isRequired,
    noUnitTranlsation: PropTypes.string.isRequired,
    confirmNoUnitNumber: PropTypes.bool.isRequired,
    handleStateOptionSelect: PropTypes.func.isRequired,
    selectedStateOption: PropTypes.shape({
        text: PropTypes.string,
        value: PropTypes.string
    }).isRequired,
    isAddressV2: PropTypes.bool.isRequired
};

FieldR.defaultProps = {
    statesList: null,
    citiesList: null,
    selectedCityOption: null,
    handleCityOptionSelect: null
};

const Fields = (props) => {
    const {
        unitNumberValidation,
        unitCheckEnabled,
        address,
        countryList,
        addressValidation,
        serverAddressValidation,
        handleAddressFieldChange,
        getTranslation,
        setConfirmNoUnitNumber,
        confirmNoUnitNumber,
        brand,
        locale,
        useGeoFeeds,
        isConventionalLodgingFlow,
        isAddressV2
    } = props;

    const {
        country: selectedCountry,
        stateProvince: selectedState,
        city: selectedCity
    } = address;

    const { order, ignoreRTL } = getFieldOrder(selectedCountry);


    const [statesList, setStatesList] = useState(null);
    const [citiesList, setCitiesList] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [isLoadingData, setIsLoadingData] = useState(false);

    const fetchAllCities = async () => {
        setIsLoadingData(true);
        const result = (await retrieveData(FETCH_COUNTRY_DATA, {
            country: selectedCountry,
            locale
        })).data.allEntitiesFetch;
        setIsLoadingData(false);
        return result;
    };

    const [selectedCityOption, setSelectedCityOption] = useState(emptyCityOption);
    const [selectedStateOption, setSelectedStateOption] = useState(emptyOption);

    useDidMountEffect(() => {
        const {
            value,
            stateId
        } = selectedCityOption;
        // set city value
        if (stateId) {
            // set selectedState option
            setSelectedStateOption(statesList.find(state => state.value.includes(`${stateId}|`)));
        }
        handleAddressFieldChange({
            target: { name: ADDRESS_FIELDS.CITY, value }
        });
    }, [selectedCityOption]);
    useDidMountEffect(() => {
        // If there is a selected city, try find it in the catalog
        // If not, an empty option will be set
        setTimeout(() => {
            setSelectedCityOption(
                (selectedCity && citiesList && citiesList.find(
                    city => city.localization.toLocaleLowerCase()
                        .includes(selectedCity.toLocaleLowerCase())
                )) || emptyCityOption
            );
        }, 200);
    }, [citiesList]);

    const handleCityOptionSelect = (cityOption) => {
        setSelectedCityOption({
            ...cityOption,
            value: cityOption.customOption ? cityOption.localization : cityOption.value
        });
    };

    useDidMountEffect(() => {
        const handleSelectedStatechange = async () => {
            const {
                value,
                text
            } = selectedStateOption;
            const [id, val] = value.split('|');
            if (!val) {
                // if not found, fetch all the country cities
                const {
                    cities: citiesResults
                } = await fetchAllCities();
                setCitiesList(getCityOptions(citiesResults, isConventionalLodgingFlow));
            } else {
                // If state is selected, cities from that state are fetched
                const stateCities = (await retrieveData(FETCH_ENTITY, {
                    entityType: 'state',
                    entityId: id,
                    locale
                })).data.entitiesFetch;
                const cityListOptions = getCityOptions(stateCities,
                    isConventionalLodgingFlow, text);
                setCitiesList(cityListOptions);
            }
            // eslint-disable-next-line max-len
            handleAddressFieldChange({ target: { name: ADDRESS_FIELDS.STATE_PROVINCE, value: val } });
        };
        handleSelectedStatechange();
    }, [selectedStateOption]);
    useDidMountEffect(() => {
        if (!useGeoFeeds) return;
        // If there is a selected state, try to find it in catalog
        // if not, an empty option will be set
        const newSelectedState = (selectedState && statesList && statesList.find(
            state => state.value.split('|')[1] === selectedState
        ))
        || (selectedState && statesList && statesList.find(
            state => state.text.toLocaleLowerCase()
                .includes(selectedState.toLocaleLowerCase())
        ))
        || Object.assign({}, emptyOption);
        setSelectedStateOption(newSelectedState);
    }, [statesList]);

    const handleStateOptionSelect = (e) => {
        const { value, selectedOptions } = e.target;
        setSelectedStateOption({
            value,
            text: selectedOptions[0].text
        });
    };
    const onCountryOptionChange = async () => {
        // when changing a country, the statelist is refreshed
        const countryStates = (await retrieveData(FETCH_ENTITY, {
            entityType: 'country',
            entityId: selectedCountry,
            locale
        })).data.entitiesFetch;
        setStatesList(getStateOptions(countryStates, isConventionalLodgingFlow));
    };
    useEffect(() => {
        if (useGeoFeeds) {
            onCountryOptionChange();
        } else {
            const states = addressFormConfig.getRegions((selectedCountry || ''));
            const list = states
            && Object.keys(states).map(region => ({
                text: states[region],
                value: region
            }));
            if (list) list.unshift({ text: '---', value: '' });
            setStatesList(list);
        }
    }, [selectedCountry]);

    const getFieldTranslation = suffix => getTranslation(`Vrbo.lyp.manualLocation.${suffix}`);

    // eslint-disable-next-line max-len
    const displayUNCheckbox = () => (unitNumberValidation.value.missingUnitNumber || !unitNumberValidation.value.unitNumberValid)
    && !unitNumberValidation.start
    && unitNumberValidation.success
    && unitCheckEnabled;

    const classes = cn('form-row', { 'form-row-ignore-rtl': ignoreRTL });
    const rows = order.map((row) => {
        const items = row.map((fieldLabelKey) => {
            const addressFieldKey = keysToFields[fieldLabelKey];
            return (
                <FieldR
                    addressFieldKey={addressFieldKey}
                    value={address[addressFieldKey] || ''}
                    fieldError={addressValidation[addressFieldKey].error || serverAddressValidation[addressFieldKey]?.error || ''}
                    key={fieldLabelKey}
                    labelTranslation={getFieldTranslation(fieldLabelKey) || ''}
                    onChange={handleAddressFieldChange}
                    countryList={countryList}
                    statesList={statesList}
                    citiesList={citiesList}
                    selectedCityOption={selectedCityOption}
                    handleCityOptionSelect={handleCityOptionSelect}
                    useGeoFeeds={useGeoFeeds}
                    brand={brand}
                    setConfirmNoUnitNumber={setConfirmNoUnitNumber}
                    displayUNCheckbox={displayUNCheckbox}
                    noUnitTranlsation={getFieldTranslation(address[addressFieldKey] ? 'unitNumberConfirmation' : 'noUnitConfirmation')}
                    confirmNoUnitNumber={confirmNoUnitNumber}
                    handleStateOptionSelect={handleStateOptionSelect}
                    selectedStateOption={selectedStateOption}
                    isAddressV2={isAddressV2}
                />
            );
        });
        return (
            <div key={row[0]}>
                <div className={classes}>
                    {items}
                </div>
                <div className="break" />
            </div>
        );
    });
    return (
        <div className="ulx-form-area">
            {rows}
        </div>
    );
};

Fields.propTypes = {
    unitNumberValidation: PropTypes.shape({
        value: PropTypes.bool,
        start: PropTypes.bool,
        success: PropTypes.bool,
        fail: PropTypes.bool
    }).isRequired,
    unitCheckEnabled: PropTypes.bool.isRequired,
    serverAddressValidation: PropTypes.shape({
        address1: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        address2: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        city: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        stateProvince: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        postalCode: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        country: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        generalState: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            errorList: PropTypes.arrayOf(PropTypes.string)
        }),
        pinMap: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        })
    }).isRequired,
    addressValidation: PropTypes.shape({
        address1: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        address2: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        city: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        stateProvince: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        postalCode: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        country: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        })
    }).isRequired,
    address: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        stateProvince: PropTypes.string,
        postalCode: PropTypes.string,
        country: PropTypes.string
    }).isRequired,
    countryList: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string
    })).isRequired,
    handleAddressFieldChange: PropTypes.func.isRequired,
    getTranslation: PropTypes.func.isRequired,
    setConfirmNoUnitNumber: PropTypes.func.isRequired,
    confirmNoUnitNumber: PropTypes.bool.isRequired,
    brand: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    useGeoFeeds: PropTypes.bool.isRequired,
    isConventionalLodgingFlow: PropTypes.bool.isRequired,
    isAddressV2: PropTypes.bool.isRequired
};

export default Fields;
